import { useState } from "react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { type AdminUserSummary, useUpdateUser } from "@/lib/api";
import { logger } from "@/lib/logger";
import { useUser } from "@/lib/user-context";

interface UserEditDialogProps {
  user: AdminUserSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getInitials(user: AdminUserSummary): string {
  if (user.first_name && user.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  if (user.full_name) {
    return user.full_name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return (user.email[0] ?? "?").toUpperCase();
}

export function UserEditDialog({ user, open, onOpenChange }: UserEditDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription className="sr-only">Edit profile for {user?.email}</DialogDescription>
        </DialogHeader>
        {user && open && (
          // Remount with fresh initial state whenever the target user changes,
          // so the form fields reflect the new user without a sync effect.
          <UserEditForm key={user.id} user={user} onCancel={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface UserEditFormProps {
  user: AdminUserSummary;
  onCancel: () => void;
}

function UserEditForm({ user, onCancel }: UserEditFormProps) {
  const { user: currentUser, refresh: refreshCurrentUser } = useUser();
  const updateUser = useUpdateUser();
  const [firstName, setFirstName] = useState(user.first_name ?? "");
  const [lastName, setLastName] = useState(user.last_name ?? "");
  const [isAdmin, setIsAdmin] = useState(user.role === "admin");
  const [error, setError] = useState("");
  const saving = updateUser.isPending;

  const handleSave = async () => {
    if (!user) {
      return;
    }
    setError("");

    try {
      await updateUser.mutateAsync({
        userId: user.id,
        firstName: firstName || null,
        lastName: lastName || null,
        role: isAdmin ? "admin" : "user",
      });
      onCancel();
      // Refresh the nav bar if the admin edited their own profile
      if (currentUser?.email === user.email) {
        await refreshCurrentUser();
      }
    } catch (err) {
      logger.error("[user-edit] save failed:", err);
      setError("Failed to update user. Please try again.");
    }
  };

  return (
    <DialogBody>
      <div className="flex items-center gap-3 pb-1">
        <Avatar size="sm">
          <AvatarFallback className="bg-accent-light text-primary text-[10px] font-semibold">
            {getInitials(user)}
          </AvatarFallback>
        </Avatar>
        <span className="text-muted-foreground text-sm">{user.email}</span>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="edit-first-name">First name</Label>
            <Input
              id="edit-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-last-name">Last name</Label>
            <Input
              id="edit-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
            />
          </div>
        </div>

        <div className="border-border flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label htmlFor="edit-admin-toggle" className="text-sm font-medium">
              Administrator
            </Label>
            <p className="text-muted-foreground text-xs">Grant full access to user management</p>
          </div>
          <Switch id="edit-admin-toggle" checked={isAdmin} onCheckedChange={setIsAdmin} />
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogFooter>
    </DialogBody>
  );
}
